<template>
  <div class="edit-unit-wrapper">
    <!-- 🌐 STICKY LANGUAGE TABS -->
    <v-card class="sticky-language-tabs" elevation="3">
      <div class="sticky-header-content">
        <!-- Language Tabs -->
        <div class="language-tabs-section">
          <v-tabs
            v-model="activeLang"
            density="comfortable"
            color="primary"
            class="language-tabs"
          >
            <v-tab
              v-for="lang in enabledLangs"
              :key="lang.code"
              :value="lang.code"
            >
              <v-icon class="mr-2">mdi-translate</v-icon>
              {{ lang.label }}
              <!-- Progress indicator -->
              <v-chip
                v-if="getCompletionPercentage(lang.code) >= 0"
                size="x-small"
                :color="
                  getCompletionPercentage(lang.code) === 100
                    ? 'success'
                    : getCompletionPercentage(lang.code) > 0
                    ? 'warning'
                    : 'grey'
                "
                class="ml-2"
              >
                {{ getCompletionPercentage(lang.code) }}%
              </v-chip>
            </v-tab>
          </v-tabs>
        </div>

        <!-- Action buttons -->
        <div class="action-buttons-section">
          <v-btn variant="text" size="small" @click="expandAll" class="mr-2">
            <v-icon class="mr-1">mdi-chevron-down</v-icon>
            {{ $t('units.expandAll') }}
          </v-btn>

          <v-btn variant="text" size="small" @click="collapseAll" class="mr-2">
            <v-icon class="mr-1">mdi-chevron-up</v-icon>
            {{ $t('units.collapseAll') }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-container fluid class="edit-unit-container">
      <v-flex xs12>
        <v-form class="mb-12" ref="addeditunitform">
          <v-expansion-panels v-model="openedPanel" multiple class="pa-0">
            <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
              <v-expansion-panel-title>
                <div class="font-weight-bold builder-title">
                  {{ $t('units.unitBuilder') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Title -->
                <v-text-field
                  v-model="getLC(activeLang).title"
                  :placeholder="
                    getLC('en').title || unit.title || $t('unitBuilder.enterTextHere')
                  "
                  :label="$t('unitBuilder.unitTitleLabel', { lang: activeLang.toUpperCase() })"
                  :rules="[rules.required, rules.length(3)]"
                  hide-details="auto"
                  color="green"
                  variant="outlined"
                  class="mt-2 mb-6"
                  style="min-width: 100%"
                />
                <!-- Subtitle -->
                <v-text-field
                  v-model="getLC(activeLang).subtitle"
                  :placeholder="
                    getLC('en').subtitle || unit.subtitle || $t('unitBuilder.enterTextHere')
                  "
                  :label="$t('unitBuilder.unitSubtitleLabel', { lang: activeLang.toUpperCase() })"
                  hide-details="auto"
                  color="green"
                  variant="outlined"
                  class="mb-7"
                  style="min-width: 100%"
                />

                <!-- Description -->
                <v-text-field
                  v-model="getLC(activeLang).description"
                  :placeholder="
                    getLC('en').description ||
                    unit.description ||
                    $t('unitBuilder.enterDescriptionPlaceholder')
                  "
                  :label="$t('unitBuilder.unitDescriptionLabel', { lang: activeLang.toUpperCase() })"
                  hide-details="auto"
                  color="green"
                  variant="outlined"
                  class="mb-7"
                  style="min-width: 100%"
                />

                <v-combobox
                  v-model="unitCategory"
                  :items="allCategories"
                  item-title="name"
                  :return-object="true"
                  class=""
                  :label="$t('unitBuilder.unitCategoryLabel')"
                  multiple
                  chips
                  variant="outlined"
                  color="#A0C548"
                  @update="updateCategories"
                  style="min-width: 100%"
                ></v-combobox>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel class="pt-8 pb-8 px-10 mt-4 tj-elevation-1">
              <v-expansion-panel-title>
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.assignments') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div>
                  {{ $t('unitBuilder.instructionalTextAssignments') }}
                </div>
                <!-- Listen -->
                <v-text-field
                  v-model="getLC(activeLang).listenLabel"
                  :label="$t('unitBuilder.actionLabel', { lang: activeLang.toUpperCase() })"
                  :placeholder="$t('unitBuilder.exampleListen')"
                  variant="underlined"
                  hide-details="auto"
                  color="green"
                  class="mt-5"
                  style="min-width: 100%"
                />
                <CustomQuill
                  :key="`assign-listen-${unit?.id || 'no-id'}-${activeLang}`"
                  :id="`assignment-editor-listen-${activeLang}`"
                  class="editor-2 mb-6 sml-editor"
                  :model-value="getAssignmentEditorValue('listen')"
                  @update:modelValue="
                    (v) => {
                      // Normalize empty HTML to empty string
                      const normalized = normalizeEmptyHtml(v)
                      getLC(activeLang).listen = normalized
                    }
                  "
                  :placeholder="$t('unitBuilder.enterTextHereWithLang', { lang: activeLang.toUpperCase() })"
                />

                <!-- Complete -->
                <v-text-field
                  v-model="getLC(activeLang).completeLabel"
                  :label="$t('unitBuilder.actionLabel', { lang: activeLang.toUpperCase() })"
                  :placeholder="$t('unitBuilder.exampleComplete')"
                  variant="underlined"
                  hide-details="auto"
                  color="green"
                  style="min-width: 100%"
                />
                <CustomQuill
                  :key="`assign-complete-${unit?.id || 'no-id'}-${activeLang}`"
                  :id="`assignment-editor-complete-${activeLang}`"
                  class="editor-3 mb-8 sml-editor"
                  :model-value="getAssignmentEditorValue('complete')"
                  @update:modelValue="
                    (v) => {
                      // Normalize empty HTML to empty string
                      const normalized = normalizeEmptyHtml(v)
                      getLC(activeLang).complete = normalized
                    }
                  "
                  :placeholder="$t('unitBuilder.enterTextHereWithLang', { lang: activeLang.toUpperCase() })"
                />

                <!-- Continue -->
                <v-text-field
                  v-model="getLC(activeLang).continueLearningLabel"
                  :label="$t('unitBuilder.actionLabel', { lang: activeLang.toUpperCase() })"
                  :placeholder="$t('unitBuilder.exampleContinue')"
                  variant="underlined"
                  hide-details="auto"
                  color="green"
                  style="min-width: 100%"
                />
                <CustomQuill
                  :key="`assign-continue-${unit?.id || 'no-id'}-${activeLang}`"
                  :id="`assignment-editor-continue-${activeLang}`"
                  class="editor-4 mb-8 sml-editor"
                  :model-value="getAssignmentEditorValue('continueLearning')"
                  @update:modelValue="
                    (v) => {
                      // Normalize empty HTML to empty string
                      const normalized = normalizeEmptyHtml(v)
                      getLC(activeLang).continueLearning = normalized
                    }
                  "
                  :placeholder="$t('unitBuilder.enterTextHereWithLang', { lang: activeLang.toUpperCase() })"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.truth') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedTruthPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.versesAndQuotes') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextVersesQuotes') }}
                      </div>
                      <VersesQuotes
                        :key="`versesQuotes-truth-${activeLang}`"
                        :backdoor="backdoor"
                        :section="`truthVersesQuotes_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).truthVersesQuotes"
                        @allversequotes="versesQuotesChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">{{ $t('unitBuilder.inBible') }}</div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextInBible') }}
                      </div>
                      <!-- TRUTH › In Bible -->
                      <CustomQuill
                        :key="`truthInBible-${activeLang}`"
                        :id="`editor4-${activeLang}`"
                        :ref="`editor4_${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="getRichTextEditorValue('truthInBible')"
                        @update:modelValue="
                          (val) =>
                            (getLC(activeLang).truthInBible =
                              normalizeEmptyHtml(val))
                        "
                        :placeholder="$t('unitBuilder.enterDescriptionHereWithLang', { lang: activeLang.toUpperCase() })"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.studyQuestionsSectionTitle') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextStudyQuestionsTruth') }}
                      </div>
                      <StudyQuestions
                        :key="`studyQuestions-truth-${activeLang}`"
                        :unitbackdoor="unitbackdoor"
                        :backdoor="backdoor"
                        :section="`truthStudyQuestions_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).truthStudyQuestions"
                        @allstudyquestions="studyQuestionsChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">{{ $t('unitBuilder.bodyContent') }}</div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextBodyContentTruth') }}
                      </div>
                      <CustomQuill
                        :key="`truthBody-${activeLang}`"
                        :id="`editor-truthBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="getRichTextEditorValue('truthBodyContent')"
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).truthBodyContent =
                              normalizeEmptyHtml(v))
                        "
                        :placeholder="$t('unitBuilder.enterDescriptionHereWithLang', { lang: activeLang.toUpperCase() })"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.equipping') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedEquipPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">{{ $t('unitBuilder.bodyContent') }}</div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextBodyContentEquipping') }}
                      </div>
                      <CustomQuill
                        :key="`equipBody-${activeLang}`"
                        :id="`editor-equipBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="getRichTextEditorValue('equipBodyContent')"
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).equipBodyContent =
                              normalizeEmptyHtml(v))
                        "
                        :placeholder="$t('unitBuilder.enterDescriptionHereWithLang', { lang: activeLang.toUpperCase() })"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.studyQuestionsSectionTitle') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextStudyQuestionsEquipping') }}
                      </div>
                      <StudyQuestions
                        :key="`studyQuestions-equip-${activeLang}`"
                        :unitbackdoor="unitbackdoor"
                        :backdoor="backdoor"
                        :section="`equipStudyQuestions_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).equipStudyQuestions"
                        @allstudyquestions="studyQuestionsChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.accountability') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedAccountPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">{{ $t('unitBuilder.bodyContent') }}</div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextBodyContentAccountability') }}
                      </div>
                      <CustomQuill
                        :key="`accountBody-${activeLang}`"
                        :id="`editor-accountBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="getRichTextEditorValue('accountBodyContent')"
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).accountBodyContent =
                              normalizeEmptyHtml(v))
                        "
                        :placeholder="$t('unitBuilder.enterDescriptionHereWithLang', { lang: activeLang.toUpperCase() })"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.studyQuestionsSectionTitle') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextStudyQuestionsAccountability') }}
                      </div>
                      <StudyQuestions
                        :key="`studyQuestions-account-${activeLang}`"
                        :unitbackdoor="unitbackdoor"
                        :backdoor="backdoor"
                        :section="`accountStudyQuestions_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).accountStudyQuestions"
                        @allstudyquestions="studyQuestionsChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.footnotes') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedFootnotesPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-text>
                      <CustomQuill
                        :key="`footnotesBody-${activeLang}`"
                        :id="`editor-footnotesBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="
                          getRichTextEditorValue('footnotesBodyContent')
                        "
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).footnotesBodyContent =
                              normalizeEmptyHtml(v))
                        "
                        :placeholder="$t('unitBuilder.enterContentHereWithLang', { lang: activeLang.toUpperCase() })"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.copyright') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedCopyrightPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-text>
                      <CustomQuill
                        :key="`copyrightBody-${activeLang}`"
                        :id="`editor-copyrightBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="
                          getRichTextEditorValue('copyrightBodyContent')
                        "
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).copyrightBodyContent =
                              normalizeEmptyHtml(v))
                        "
                        :placeholder="$t('unitBuilder.enterContentHereWithLang', { lang: activeLang.toUpperCase() })"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-progress-linear
            v-if="isEditingUnit"
            :indeterminate="isEditingUnit"
            :active="isEditingUnit"
            color="#592892"
            class="mb-5"
          ></v-progress-linear>
          <div class="text-center">
            <div v-if="checkAuthorization(['superAdmin']).length > 0">
              <div>
                {{ $t('units.churchAvailabilityQuestion') }}
              </div>
              <div class="d-flex justify-center mb-4">
                <v-radio-group
                  v-model="unit.type"
                  inline
                  :disabled="!checkAuthorization(['superAdmin']).length > 0"
                >
                  <v-radio :label="$t('units.yes')" value="public"></v-radio>
                  <v-radio :label="$t('units.no')" value="private"></v-radio>
                </v-radio-group>
              </div>
            </div>
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold mb-14 px-14"
              :loading="isEditingUnit"
              :disabled="isEditingUnit"
              @click="editUnit('draft')"
              >{{ $t('units.saveAsDraft') }}
              <template #loader>
                <span>{{ $t('unitBuilder.saving') }}</span>
                <span class="custom-loader">
                  <v-icon>cached</v-icon>
                </span>
              </template>
            </v-btn>
            <v-btn
              color="#592892"
              class="elevation-0 font-weight-bold ml-7 mb-14 px-14"
              :loading="isEditingUnit"
              :disabled="isEditingUnit"
              @click="editUnit('published')"
              >{{ $t('units.publishChanges') }}
              <template #loader>
                <span>{{ $t('unitBuilder.saving') }}</span>
                <span class="custom-loader">
                  <v-icon>cached</v-icon>
                </span>
              </template>
            </v-btn>
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold ml-7 mb-14 px-14"
              :loading="isPreviewingUnit"
              :disabled="isPreviewingUnit"
              @click="previewUnit(unit)"
              >{{ $t('units.previewUnit') }}
              <template #loader>
                <span>{{ $t('unitBuilder.loading') }}</span>
                <span class="custom-loader">
                  <v-icon>cached</v-icon>
                </span>
              </template>
            </v-btn>
          </div>
          <v-alert
            v-if="feedback"
            border="start"
            color="#592892"
            class="text-center"
          >
            {{ feedback }}
          </v-alert>
          <v-divider class="dashed"></v-divider>
        </v-form>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import { mapGetters } from 'vuex'
  import StudyQuestions from '@/components/utilities/StudyQuestions'
  import VersesQuotes from '@/components/utilities/VersesQuotes'
  import CustomQuill from '@/components/utilities/CustomQuill.vue'
  import { useLanguages } from '@/helpers/useLanguages'
  import { openPreview } from '@/helpers/previewBridge'

  export default {
    name: 'EditUnit',
    props: ['unit', 'unitbackdoor', 'allCategories'],
    components: {
      StudyQuestions,
      VersesQuotes,
      CustomQuill,
    },
    setup() {
      /* if you’re using <script setup>, otherwise in options API, use created()/computed() */
      const { enabledLangs, defaultLang } = useLanguages()
      return { enabledLangs, defaultLang }
    },
    data() {
      return {
        feedback: null,
        openedPanel: [0, 1, 2, 3, 4, 5, 6],
        openedTruthPanels: [0, 1, 2, 3],
        openedEquipPanels: [0, 1],
        openedAccountPanels: [0, 1],
        openedFootnotesPanels: [0],
        openedCopyrightPanels: [0],
        isEditingUnit: false,
        isPreviewingUnit: false,
        thePreviewUnit: [],
        unitCategory: [],
        newCategory: [],
        backdoor: 0,
        completionScore: 0,
        activeLang: 'en',
        localUnit: null, // Local working copy to avoid mutating prop/Vuex state
      }
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      rules() {
        return {
          length: (len) => (v) =>
            (v || '').length >= len || this.$t('unitBuilder.minCharactersRequired', { len }),
          required: (v) => !!v || this.$t('unitBuilder.fieldRequired'),
        }
      },
    },
    watch: {
      unit: function (newVal, oldVal) {
        // watch it
        // console.log('unit changed: ', newVal, ' | was: ', oldVal)
        if (newVal !== oldVal) {
          console.log('unit changed: ', newVal, ' | was: ', oldVal)
          // When switching to a different unit, reset local editor cache.
          // This prevents localizedContent from a prior unit bleeding into the new one.
          if (!oldVal || newVal?.id !== oldVal?.id) {
            this.localUnit = null
          }

          // re-init component
          this.init()
          // make sure all modules are expanded
          this.expandAll()
        }
      },
    },
    methods: {
      editUnit(status) {
        this.feedback = ''

        const lcEN = this.getLC('en')
        const derivedTitle = (lcEN && lcEN.title) || this.unit.title || ''

        if (!derivedTitle) {
          this.feedback = this.$t('unitBuilder.needUnitTitle')
          return
        }

        this.isEditingUnit = true

        // filter out any new categories without IDs
        const updatedUnitCategory = this.unitCategory.filter(
          (item) => item.id !== undefined
        )

        // if there was a new category add it to the collection
        if (this.newCategory.length) {
          for (let index = 0; index < this.newCategory.length; index++) {
            const cat = this.newCategory[index]
            db.collection('categories')
              .add({ name: cat })
              .then((docRef) => {
                updatedUnitCategory.push({ id: docRef.id })
              })
          }
        }

        // remove the name property from the section category array before adding it to the collection
        updatedUnitCategory.map(function (item) {
          delete item.name
          return item
        })

        // debounce
        if (this.timeout) clearTimeout(this.timeout)

        this.timeout = setTimeout(() => {
          // Get localizedContent.en for this save operation
          const lcEN = this.localUnit?.localizedContent?.en || this.unit.localizedContent?.en || {}
          
          // 🟢 Pull from localizedContent › en (fallback to legacy)
          const titleFinal = derivedTitle
          const subtitleFinal =
            (lcEN && lcEN.subtitle) || this.unit.subtitle || ''
          const descriptionFinal =
            (lcEN && lcEN.description) || this.unit.description || ''

          // Check if property exists in localizedContent (even if empty string)
          // Only fallback to top-level if property doesn't exist in localizedContent
          const listenLabelFinal = (lcEN && 'listenLabel' in lcEN) ? (lcEN.listenLabel || 'Listen') : (this.unit.listenLabel || 'Listen')
          const completeLabelFinal = (lcEN && 'completeLabel' in lcEN) ? (lcEN.completeLabel || 'Complete') : (this.unit.completeLabel || 'Complete')
          const continueLearningLabelFinal = (lcEN && 'continueLearningLabel' in lcEN) ? (lcEN.continueLearningLabel || 'Continue') : (this.unit.continueLearningLabel || 'Continue')

          // Check if property exists in localizedContent (even if empty string)
          // Normalize empty HTML to empty string before using
          // Only fallback to top-level if property doesn't exist in localizedContent
          const getNormalizedValue = (key) => {
            if (lcEN && (key in lcEN)) {
              const normalized = this.normalizeEmptyHtml(lcEN[key])
              return normalized === '' ? '' : normalized
            }
            return this.unit[key] || ''
          }
          const listenFinal = getNormalizedValue('listen')
          const completeFinal = getNormalizedValue('complete')
          const continueLearningFinal = getNormalizedValue('continueLearning')

          const truthInBibleCont =
            (lcEN && lcEN.truthInBible) || this.unit.truthInBible || ''
          const truthCont =
            (lcEN && lcEN.truthBodyContent) || this.unit.truthBodyContent || ''
          const equipCont =
            (lcEN && lcEN.equipBodyContent) || this.unit.equipBodyContent || ''
          const accountCont =
            (lcEN && lcEN.accountBodyContent) ||
            this.unit.accountBodyContent ||
            ''
          const footCont =
            (lcEN && lcEN.footnotesBodyContent) ||
            this.unit.footnotesBodyContent ||
            ''
          const copyCont =
            (lcEN && lcEN.copyrightBodyContent) ||
            this.unit.copyrightBodyContent ||
            ''

          // Get study questions and verses/quotes from localizedContent (EN)
          let truthQues = []
          let equipQues = []
          let accountQues = []
          let truthVrsQts = []

          if (
            lcEN &&
            lcEN.truthStudyQuestions &&
            Array.isArray(lcEN.truthStudyQuestions) &&
            lcEN.truthStudyQuestions.length > 0
          ) {
            truthQues = lcEN.truthStudyQuestions
          } else if (
            this.unit.truthStudyQuestions &&
            Object.keys(this.unit.truthStudyQuestions).length > 0
          ) {
            // Fallback to legacy top-level property
            truthQues = this.unit.truthStudyQuestions
          }

          if (
            lcEN &&
            lcEN.equipStudyQuestions &&
            Array.isArray(lcEN.equipStudyQuestions) &&
            lcEN.equipStudyQuestions.length > 0
          ) {
            equipQues = lcEN.equipStudyQuestions
          } else if (
            this.unit.equipStudyQuestions &&
            Object.keys(this.unit.equipStudyQuestions).length > 0
          ) {
            // Fallback to legacy top-level property
            equipQues = this.unit.equipStudyQuestions
          }

          if (
            lcEN &&
            lcEN.accountStudyQuestions &&
            Array.isArray(lcEN.accountStudyQuestions) &&
            lcEN.accountStudyQuestions.length > 0
          ) {
            accountQues = lcEN.accountStudyQuestions
          } else if (
            this.unit.accountStudyQuestions &&
            Object.keys(this.unit.accountStudyQuestions).length > 0
          ) {
            // Fallback to legacy top-level property
            accountQues = this.unit.accountStudyQuestions
          }

          if (
            lcEN &&
            lcEN.truthVersesQuotes &&
            Array.isArray(lcEN.truthVersesQuotes) &&
            lcEN.truthVersesQuotes.length > 0
          ) {
            truthVrsQts = lcEN.truthVersesQuotes
          } else if (
            this.unit.truthVersesQuotes &&
            Object.keys(this.unit.truthVersesQuotes).length > 0
          ) {
            // Fallback to legacy top-level property
            truthVrsQts = this.unit.truthVersesQuotes
          }

          // Completion score
          this.completionScore = 0
          if (titleFinal) this.completionScore++
          if (subtitleFinal) this.completionScore++
          if (listenFinal) this.completionScore++
          if (completeFinal) this.completionScore++
          if (continueLearningFinal) this.completionScore++
          if (truthVrsQts.length) this.completionScore++
          if (truthQues.length) this.completionScore++
          if (truthCont) this.completionScore++
          if (truthInBibleCont) this.completionScore++
          if (equipQues.length) this.completionScore++
          if (equipCont) this.completionScore++
          if (accountCont) this.completionScore++
          if (accountQues.length) this.completionScore++

          const verified =
            this.checkAuthorization(['superAdmin']).length > 0 ||
            this.unit.type == 'private'
              ? 'verified'
              : 'unverified'

          // Ensure localizedContent.en structure exists and has assignment fields
          // This ensures empty strings are explicitly saved when assignments are cleared
          const lcToSave = this.localUnit?.localizedContent || this.unit.localizedContent || {}
          if (!lcToSave.en) {
            lcToSave.en = {}
          }
          // Explicitly set assignment fields in localizedContent.en (even if empty)
          // Normalize empty HTML to empty string before saving
          // This ensures cleared assignments are saved as empty strings, not empty HTML
          // Note: lcEN is already defined at the beginning of this setTimeout block
          if ('listen' in lcEN) {
            const normalized = this.normalizeEmptyHtml(lcEN.listen)
            lcToSave.en.listen = normalized === '' ? '' : normalized
          }
          if ('complete' in lcEN) {
            const normalized = this.normalizeEmptyHtml(lcEN.complete)
            lcToSave.en.complete = normalized === '' ? '' : normalized
          }
          if ('continueLearning' in lcEN) {
            const normalized = this.normalizeEmptyHtml(lcEN.continueLearning)
            lcToSave.en.continueLearning = normalized === '' ? '' : normalized
          }
          if ('listenLabel' in lcEN) {
            lcToSave.en.listenLabel = lcEN.listenLabel || ''
          }
          if ('completeLabel' in lcEN) {
            lcToSave.en.completeLabel = lcEN.completeLabel || ''
          }
          if ('continueLearningLabel' in lcEN) {
            lcToSave.en.continueLearningLabel = lcEN.continueLearningLabel || ''
          }

          // 🔥 Firestore save
          db.collection('units')
            .doc(this.unit.id)
            .set({
              title: titleFinal,
              subtitle: subtitleFinal,
              category: updatedUnitCategory,
              listen: listenFinal,
              listenLabel: listenLabelFinal,
              complete: completeFinal,
              completeLabel: completeLabelFinal,
              continueLearning: continueLearningFinal,
              continueLearningLabel: continueLearningLabelFinal,
              description: descriptionFinal,
              truthVersesQuotes: truthVrsQts,
              truthStudyQuestions: truthQues,
              truthBodyContent: truthCont,
              truthInBible: truthInBibleCont,
              equipStudyQuestions: equipQues,
              equipBodyContent: equipCont,
              accountBodyContent: accountCont,
              accountStudyQuestions: accountQues,
              completionScore: this.completionScore,
              footnotesBodyContent: footCont,
              copyrightBodyContent: copyCont,
              localizedContent: lcToSave,
              status: status,
              type: this.unit.type,
              cid: this.unit.cid || '',
              verified: verified,
              updated: Date.now(),
            })
            .then(() => {
              if (verified == 'unverified' && status == 'published') {
                this.$store.dispatch('AppData/notification', {
                  leader: this.$store.getters['UserData/getUser'],
                  meta: { unitTitle: titleFinal },
                  type: 'Unit edited',
                  cid: this.$store.getters['AppData/cid'],
                })
              }

              // Update localUnit to reflect saved state (don't mutate prop/Vuex state)
              if (this.localUnit) {
                this.localUnit.title = titleFinal
                this.localUnit.subtitle = subtitleFinal
                this.localUnit.description = descriptionFinal
                this.localUnit.listen = listenFinal
                this.localUnit.listenLabel = listenLabelFinal
                this.localUnit.complete = completeFinal
                this.localUnit.completeLabel = completeLabelFinal
                this.localUnit.continueLearning = continueLearningFinal
                this.localUnit.continueLearningLabel = continueLearningLabelFinal
                this.localUnit.truthInBible = truthInBibleCont
                this.localUnit.truthBodyContent = truthCont
                this.localUnit.equipBodyContent = equipCont
                this.localUnit.accountBodyContent = accountCont
                this.localUnit.footnotesBodyContent = footCont
                this.localUnit.copyrightBodyContent = copyCont
              }

              this.backdoor++
              this.$emit('closeEditUnitsForm', {
                closeEditUnitsForm: true,
                title: titleFinal,
              })
              this.isEditingUnit = false
            })
        }, 300)
      },
      previewUnit() {
        // Get verses and questions from localizedContent for the currently selected language
        let truthQues =
          this.getLC(this.activeLang).truthStudyQuestions &&
          Array.isArray(this.getLC(this.activeLang).truthStudyQuestions) &&
          this.getLC(this.activeLang).truthStudyQuestions.length > 0
            ? this.getLC(this.activeLang).truthStudyQuestions
            : []
        let equipQues =
          this.getLC(this.activeLang).equipStudyQuestions &&
          Array.isArray(this.getLC(this.activeLang).equipStudyQuestions) &&
          this.getLC(this.activeLang).equipStudyQuestions.length > 0
            ? this.getLC(this.activeLang).equipStudyQuestions
            : []
        let accountQues =
          this.getLC(this.activeLang).accountStudyQuestions &&
          Array.isArray(this.getLC(this.activeLang).accountStudyQuestions) &&
          this.getLC(this.activeLang).accountStudyQuestions.length > 0
            ? this.getLC(this.activeLang).accountStudyQuestions
            : []
        let truthVrsQts =
          this.getLC(this.activeLang).truthVersesQuotes &&
          Array.isArray(this.getLC(this.activeLang).truthVersesQuotes) &&
          this.getLC(this.activeLang).truthVersesQuotes.length > 0
            ? this.getLC(this.activeLang).truthVersesQuotes
            : []

        // build preview unit with currently selected language content
        // Only fallback to English for non-language-specific fields (id, category, etc.)
        const lcActive = this.getLC(this.activeLang)
        // Helper to get value: use language-specific if it exists (even if empty), otherwise empty
        const getLangValue = (key) => {
          // Check if this is an assignment field - these should NEVER fall back to top-level
          const isAssignmentField = key === 'listen' || key === 'complete' || key === 'continueLearning' || 
                                   key === 'listenLabel' || key === 'completeLabel' || key === 'continueLearningLabel'
          
          // Direct check: does the language-specific content have this key?
          // Use 'in' operator to check if property exists on the object
          const hasProperty = key in lcActive
          
          // If the property exists in localizedContent, use it (even if empty string)
          // This ensures cleared assignments stay cleared in preview
          if (hasProperty) {
            const value = lcActive[key]
            // For assignment fields, normalize empty HTML
            if (isAssignmentField) {
              if (!value || value === '' || value === null || value === undefined) {
                return ''
              }
              const trimmed = String(value).replace(/<[^>]*>/g, '').trim()
              if (!trimmed || trimmed === '') {
                return ''
              }
            }
            return value || ''
          }
          
          // For assignment fields, NEVER fall back to top-level (even for English)
          // This ensures we only show content from localizedContent
          if (isAssignmentField) {
            return ''
          }
          
          // For non-assignment fields, fallback to English legacy fields if previewing English
          if (this.activeLang === 'en') {
            return this.unit[key] || ''
          }
          
          // For non-English languages, return empty string (don't show English content)
          return ''
        }
        
        // Build preview unit - don't spread unit to avoid English fallback pollution
        const previewUnit = {
          // Copy non-content properties
          id: this.unit.id,
          category: this.unit.category,
          status: this.unit.status,
          type: this.unit.type,
          cid: this.unit.cid,
          verified: this.unit.verified,
          updated: this.unit.updated,
          completionScore: this.unit.completionScore,
          // Use language-specific content, only fallback to English if activeLang is 'en'
          title: getLangValue('title'),
          subtitle: getLangValue('subtitle'),
          description: getLangValue('description'),
          listen: getLangValue('listen'),
          listenLabel: getLangValue('listenLabel'),
          complete: getLangValue('complete'),
          completeLabel: getLangValue('completeLabel'),
          continueLearning: getLangValue('continueLearning'),
          continueLearningLabel: getLangValue('continueLearningLabel'),
          truthVersesQuotes: truthVrsQts,
          truthStudyQuestions: truthQues,
          truthBodyContent: getLangValue('truthBodyContent'),
          truthInBible: getLangValue('truthInBible'),
          equipStudyQuestions: equipQues,
          equipBodyContent: getLangValue('equipBodyContent'),
          accountBodyContent: getLangValue('accountBodyContent'),
          accountStudyQuestions: accountQues,
          footnotesBodyContent: getLangValue('footnotesBodyContent'),
          copyrightBodyContent: getLangValue('copyrightBodyContent'),
          localizedContent: this.localUnit?.localizedContent || this.unit.localizedContent || {},
        }

        const opened = openPreview(this.$router, this.$route, {
          name: 'PreviewUnit',
          lang: this.activeLang || 'en',
          payload: previewUnit,
        })
        if (!opened) {
          this.feedback =
            'Preview could not be opened. Free some browser storage and try again.'
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const hasRole = permissions.filter(() =>
            userRoles.some((role) => permissions.includes(role))
          )
          return hasRole
        }
      },
      collapseAll() {
        this.openedPanel = []
        this.openedTruthPanels = []
        this.openedEquipPanels = []
        this.openedAccountPanels = []
        this.openedFootnotesPanels = []
        this.openedCopyrightPanels = []
      },
      expandAll() {
        this.openedPanel = [0, 1, 2, 3, 4, 5, 6]
        this.openedTruthPanels = [0, 1, 2, 3]
        this.openedEquipPanels = [0, 1]
        this.openedAccountPanels = [0, 1]
        // Force expansion by clearing first, then setting
        this.openedFootnotesPanels = []
        this.openedCopyrightPanels = []
        this.$nextTick(() => {
          this.openedFootnotesPanels = [0]
          this.openedCopyrightPanels = [0]
        })
      },
      studyQuestionsChild(value) {
        // Extract language from section name (e.g., "truthStudyQuestions_en" -> "en")
        const lang = value.section.split('_').pop()
        const sectionName = value.section.replace(`_${lang}`, '')

        // Update the localized content
        if (sectionName === 'truthStudyQuestions') {
          this.getLC(lang).truthStudyQuestions =
            value.questions && value.questions.length ? value.questions : []
        } else if (sectionName === 'equipStudyQuestions') {
          this.getLC(lang).equipStudyQuestions =
            value.questions && value.questions.length ? value.questions : []
        } else if (sectionName === 'accountStudyQuestions') {
          this.getLC(lang).accountStudyQuestions =
            value.questions && value.questions.length ? value.questions : []
        }
      },
      versesQuotesChild(value) {
        // Extract language from section name (e.g., "truthVersesQuotes_en" -> "en")
        const lang = value.section.split('_').pop()
        const sectionName = value.section.replace(`_${lang}`, '')

        // Update the localized content
        if (sectionName === 'truthVersesQuotes') {
          this.getLC(lang).truthVersesQuotes =
            value.versesquotes && value.versesquotes.length
              ? value.versesquotes
              : []
        }
      },
      updateCategories() {
        const allCat = this.allCategories
        const unitCat = this.unitCategory

        // Find values that are in unitCat but not in allCat
        this.newCategory = unitCat.filter(function (obj) {
          return !allCat.some(function (obj2) {
            return obj.name == obj2.name
          })
        })
      },
      // Helper to normalize empty HTML to empty string
      normalizeEmptyHtml(html) {
        if (!html || html === '') return ''
        // Remove all HTML tags and check if there's any actual content
        const textOnly = String(html).replace(/<[^>]*>/g, '').trim()
        // If no text content, return empty string
        return textOnly === '' ? '' : html
      },
      // Some assignment content is authored as list HTML. In this editor path,
      // convert list blocks to paragraph lines so content remains visible.
      getAssignmentEditorValue(key) {
        const raw = this.getLC(this.activeLang)?.[key] || ''
        return this.normalizeListHtmlForEditor(raw, false)
      },
      // Normalize rich text list HTML to keep bullets visible in the editor.
      getRichTextEditorValue(key) {
        const raw = this.getLC(this.activeLang)?.[key] || ''
        return this.normalizeListHtmlForEditor(raw, true)
      },
      normalizeListHtmlForEditor(raw, includeBulletGlyph = false) {
        if (!raw || typeof raw !== 'string') return raw
        if (!/<(ul|ol)\b/i.test(raw)) return raw

        const liStart = includeBulletGlyph ? '<p>&bull; ' : '<p>'
        return raw
          .replace(/<li[^>]*>\s*/gi, liStart)
          .replace(/\s*<\/li>/gi, '</p>')
          .replace(/<\/?(ul|ol)[^>]*>/gi, '')
          .replace(/<p>\s*<\/p>/gi, '')
          .trim()
      },
      // ensure unit.localizedContent exists and return the bucket for a lang
      getLC(lang) {
        // Use localUnit to avoid mutating prop/Vuex state
        // Create localUnit if it doesn't exist yet (lazy initialization for safety)
        if (!this.localUnit) {
          this.localUnit = JSON.parse(JSON.stringify(this.unit))
        }
        
        // Ensure localizedContent exists
        if (!this.localUnit.localizedContent) {
          this.localUnit.localizedContent = {}
        }
        
        // Ensure language-specific content exists
        if (!this.localUnit.localizedContent[lang]) {
          this.localUnit.localizedContent[lang] = {}
        }
        
        return this.localUnit.localizedContent[lang]
      },
      // Calculate completion percentage for a given language
      getCompletionPercentage(lang) {
        const content = this.getLC(lang)
        const fields = [
          'title',
          'subtitle',
          'description',
          'listen',
          'complete',
          'continueLearning',
          'truthBodyContent',
          'truthInBible',
          'equipBodyContent',
          'accountBodyContent',
          'footnotesBodyContent',
          'copyrightBodyContent',
        ]

        let filledFields = 0
        fields.forEach((field) => {
          if (content[field] && content[field].trim() !== '') {
            filledFields++
          }
        })

        // Add verses and questions to completion calculation
        if (
          content.truthVersesQuotes &&
          Array.isArray(content.truthVersesQuotes) &&
          content.truthVersesQuotes.length > 0
        )
          filledFields++
        if (
          content.truthStudyQuestions &&
          Array.isArray(content.truthStudyQuestions) &&
          content.truthStudyQuestions.length > 0
        )
          filledFields++
        if (
          content.equipStudyQuestions &&
          Array.isArray(content.equipStudyQuestions) &&
          content.equipStudyQuestions.length > 0
        )
          filledFields++
        if (
          content.accountStudyQuestions &&
          Array.isArray(content.accountStudyQuestions) &&
          content.accountStudyQuestions.length > 0
        )
          filledFields++

        const totalFields = fields.length + 4 // Add 4 for verses/questions
        const percentage = Math.round((filledFields / totalFields) * 100)
        return percentage
      },
      init() {
        // Create local working copy of unit to avoid mutating prop/Vuex state.
        // Rebuild local state when unit id changes; otherwise keep in-progress edits.
        if (!this.localUnit || this.localUnit.id !== this.unit.id) {
          this.localUnit = JSON.parse(JSON.stringify(this.unit))
        }
        
        // categories (unchanged) …
        if (this.unit.category.length) {
          this.unitCategory = this.allCategories.filter((obj) =>
            this.unit.category.some((obj2) => obj.id == obj2.id)
          )
        } else {
          this.unitCategory = []
        }

        // Seed EN values from legacy once for convenience
        const lc = this.getLC('en')
        if (!lc.title && this.unit.title) lc.title = this.unit.title
        if (!lc.subtitle && this.unit.subtitle) lc.subtitle = this.unit.subtitle
        if (!lc.description && this.unit.description)
          lc.description = this.unit.description

        // Assignments (EN)
        // If localized EN assignment fields are missing OR effectively empty, seed from legacy EN.
        // This keeps builder aligned with list preview behavior during legacy -> localized transition.
        const hasMeaningfulValue = (value, isHtml = false) => {
          if (value === undefined || value === null) return false
          const str = String(value).trim()
          if (!str) return false
          if (!isHtml) return true
          return this.normalizeEmptyHtml(str) !== ''
        }
        if (!hasMeaningfulValue(lc.listenLabel) && hasMeaningfulValue(this.unit.listenLabel))
          lc.listenLabel = this.unit.listenLabel
        if (!hasMeaningfulValue(lc.completeLabel) && hasMeaningfulValue(this.unit.completeLabel))
          lc.completeLabel = this.unit.completeLabel
        if (!hasMeaningfulValue(lc.continueLearningLabel) && hasMeaningfulValue(this.unit.continueLearningLabel))
          lc.continueLearningLabel = this.unit.continueLearningLabel
        if (!hasMeaningfulValue(lc.listen, true) && hasMeaningfulValue(this.unit.listen, true))
          lc.listen = this.unit.listen
        if (!hasMeaningfulValue(lc.complete, true) && hasMeaningfulValue(this.unit.complete, true))
          lc.complete = this.unit.complete
        if (!hasMeaningfulValue(lc.continueLearning, true) && hasMeaningfulValue(this.unit.continueLearning, true))
          lc.continueLearning = this.unit.continueLearning


        // Sections
        if (!lc.truthInBible && this.unit.truthInBible)
          lc.truthInBible = this.unit.truthInBible
        if (!lc.truthBodyContent && this.unit.truthBodyContent)
          lc.truthBodyContent = this.unit.truthBodyContent
        if (!lc.equipBodyContent && this.unit.equipBodyContent)
          lc.equipBodyContent = this.unit.equipBodyContent
        if (!lc.accountBodyContent && this.unit.accountBodyContent)
          lc.accountBodyContent = this.unit.accountBodyContent
        if (!lc.footnotesBodyContent && this.unit.footnotesBodyContent)
          lc.footnotesBodyContent = this.unit.footnotesBodyContent
        if (!lc.copyrightBodyContent && this.unit.copyrightBodyContent)
          lc.copyrightBodyContent = this.unit.copyrightBodyContent

        // Migrate legacy study questions and verses/quotes to localizedContent
        // Only migrate if localizedContent doesn't already have them
        if (
          !lc.truthStudyQuestions &&
          this.unit.truthStudyQuestions &&
          Array.isArray(this.unit.truthStudyQuestions) &&
          this.unit.truthStudyQuestions.length > 0
        ) {
          lc.truthStudyQuestions = [...this.unit.truthStudyQuestions]
        }
        if (
          !lc.equipStudyQuestions &&
          this.unit.equipStudyQuestions &&
          Array.isArray(this.unit.equipStudyQuestions) &&
          this.unit.equipStudyQuestions.length > 0
        ) {
          lc.equipStudyQuestions = [...this.unit.equipStudyQuestions]
        }
        if (
          !lc.accountStudyQuestions &&
          this.unit.accountStudyQuestions &&
          Array.isArray(this.unit.accountStudyQuestions) &&
          this.unit.accountStudyQuestions.length > 0
        ) {
          lc.accountStudyQuestions = [...this.unit.accountStudyQuestions]
        }
        if (
          !lc.truthVersesQuotes &&
          this.unit.truthVersesQuotes &&
          Array.isArray(this.unit.truthVersesQuotes) &&
          this.unit.truthVersesQuotes.length > 0
        ) {
          lc.truthVersesQuotes = [...this.unit.truthVersesQuotes]
        }

        // Ensure arrays exist for all languages
        const allLangs = this.enabledLangs.map((l) => l.code)
        allLangs.forEach((lang) => {
          const langContent = this.getLC(lang)
          if (!Array.isArray(langContent.truthStudyQuestions)) {
            langContent.truthStudyQuestions = []
          }
          if (!Array.isArray(langContent.equipStudyQuestions)) {
            langContent.equipStudyQuestions = []
          }
          if (!Array.isArray(langContent.accountStudyQuestions)) {
            langContent.accountStudyQuestions = []
          }
          if (!Array.isArray(langContent.truthVersesQuotes)) {
            langContent.truthVersesQuotes = []
          }
        })
      },
    },
    created() {
      // when languages load, set the default
      // (if you kept Options API, you can watch in mounted too)
      const stop = this.$watch(
        () => this.enabledLangs,
        (langs) => {
          if (langs && langs.length) {
            this.activeLang =
              langs.find((l) => l.isDefault)?.code || langs[0].code
            stop && stop()
          }
        },
        { immediate: true, deep: true }
      )
      this.init()
    },
  }
</script>

<style>
  .sml-editor .ql-editor {
    min-height: 100px;
    max-height: 150px;
  }
  .custom-label {
    transform: scale(0.75);
    display: inline-flex;
  }

  /* Sticky language tabs */
  .sticky-language-tabs {
    position: -webkit-sticky; /* Safari */
    position: sticky;
    top: 100px;
    z-index: 1000;
    background: white;
    border-bottom: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    margin-bottom: 16px;
  }

  /* Sticky header content layout */
  .sticky-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    min-height: 64px;
  }

  /* Language tabs section */
  .language-tabs-section {
    flex: 1;
    min-width: 300px;
  }

  /* Action buttons section */
  .action-buttons-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Language tabs styling */
  .language-tabs {
    min-width: 300px;
  }

  .language-tabs .v-tab {
    min-width: 120px;
  }

  /* Wrapper for sticky positioning */
  .edit-unit-wrapper {
    position: relative;
  }

  /* Dark mode support */
  .v-theme--dark .sticky-language-tabs {
    background: #1e1e1e;
    border-bottom-color: #424242;
  }

  /* Remove old panel actions styles since we moved them to sticky header */
  .edit-unit-container .panel-actions {
    display: none;
  }

  /* Remove left padding from expansion panel titles only */
  .v-expansion-panel-title {
    padding-left: 0 !important;
  }
</style>
